import React, { useEffect, useState, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Input,
  Tooltip,
  Badge,
} from '@chakra-ui/react';
import { FiDownload, FiTrash2, FiUpload, FiCopy, FiFile } from 'react-icons/fi';
import CompaniesApi from '../../../../api/companies';
import User from '../../../../api/user';

export default function CompanyFilesModal({ isOpen, onClose, company }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();
  const toast = useToast();

  // Check if user has SUPER_ADMIN access (type === 1 or has specific admin screens)
  const userInfo = User.GetInfo();
  const isSuperAdmin = userInfo && (
    userInfo.type === 1 ||
    (userInfo.screens && userInfo.screens.includes('users') && userInfo.screens.includes('data-tables'))
  );

  useEffect(() => {
    if (isOpen && company && isSuperAdmin) {
      loadFiles();
    }
  }, [isOpen, company, isSuperAdmin]);

  const loadFiles = async () => {
    if (!company) return;
    
    setLoading(true);
    try {
      const response = await CompaniesApi.listCompanyFiles(company.id);
      if (response.data && response.data.success) {
        setFiles(response.data.data || []);
      } else {
        setFiles([]);
      }
    } catch (error) {
      console.error('Error loading files:', error);
      toast({
        title: 'שגיאה בטעינת קבצים',
        description: 'לא ניתן לטעון את רשימת הקבצים',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file || !company) return;

    setUploading(true);
    try {
      const response = await CompaniesApi.uploadCompanyFile(file, company.id);
      if (response.data && response.data.success) {
        toast({
          title: 'קובץ הועלה בהצלחה',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
        loadFiles(); // Refresh the files list
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: 'שגיאה בהעלאת קובץ',
        description: 'לא ניתן להעלות את הקובץ',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleDownload = async (fileName) => {
    if (!company) return;

    try {
      const response = await CompaniesApi.downloadCompanyFile(company.id, fileName);
      
      // Create blob and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'קובץ הורד בהצלחה',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: 'שגיאה בהורדת קובץ',
        description: 'לא ניתן להוריד את הקובץ',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const handleDelete = async (fileName) => {
    if (!company || !window.confirm(`האם אתה בטוח שברצונך למחוק את הקובץ "${fileName}"?`)) {
      return;
    }

    try {
      const response = await CompaniesApi.deleteCompanyFile(company.id, fileName);
      if (response.data && response.data.success) {
        toast({
          title: 'קובץ נמחק בהצלחה',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
        loadFiles(); // Refresh the files list
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      toast({
        title: 'שגיאה במחיקת קובץ',
        description: 'לא ניתן למחוק את הקובץ',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const handleCopyUrl = (publicUrl) => {
    navigator.clipboard.writeText(publicUrl).then(() => {
      toast({
        title: 'קישור הועתק ללוח',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
    }).catch(() => {
      toast({
        title: 'שגיאה בהעתקת קישור',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Don't render if user is not SUPER_ADMIN
  if (!isSuperAdmin) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay />
      <ModalContent maxH="80vh">
        <ModalHeader>
          קבצי חברה - {company ? company.name : ''}
        </ModalHeader>
        <ModalBody overflowY="auto">
          <VStack spacing={4} align="stretch">
            {/* Upload Section */}
            <Box>
              <HStack spacing={4} mb={4}>
                <Button
                  leftIcon={<FiUpload />}
                  colorScheme="blue"
                  onClick={handleFileUpload}
                  isLoading={uploading}
                  loadingText="מעלה..."
                >
                  העלאת קובץ
                </Button>
                <Input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </HStack>
            </Box>

            {/* Files Table */}
            {loading ? (
              <Box textAlign="center" py={8}>
                <Spinner size="lg" />
                <Text mt={2}>טוען קבצים...</Text>
              </Box>
            ) : files.length === 0 ? (
              <Alert status="info">
                <AlertIcon />
                אין קבצים עבור חברה זו
              </Alert>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>שם קובץ</Th>
                      <Th>גודל</Th>
                      <Th>תאריך העלאה</Th>
                      <Th>קישור ציבורי</Th>
                      <Th>פעולות</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {files.map((file, index) => (
                      <Tr key={index}>
                        <Td>
                          <HStack>
                            <FiFile />
                            <Text>{file.fileName}</Text>
                          </HStack>
                        </Td>
                        <Td>
                          <Badge colorScheme="gray">
                            {formatFileSize(file.size)}
                          </Badge>
                        </Td>
                        <Td>{formatDate(file.uploadedAt)}</Td>
                        <Td>
                          <Tooltip label="העתק קישור">
                            <IconButton
                              icon={<FiCopy />}
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCopyUrl(file.publicUrl)}
                            />
                          </Tooltip>
                        </Td>
                        <Td>
                          <HStack spacing={2}>
                            <Tooltip label="הורד קובץ">
                              <IconButton
                                icon={<FiDownload />}
                                size="sm"
                                colorScheme="blue"
                                variant="ghost"
                                onClick={() => handleDownload(file.fileName)}
                              />
                            </Tooltip>
                            <Tooltip label="מחק קובץ">
                              <IconButton
                                icon={<FiTrash2 />}
                                size="sm"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => handleDelete(file.fileName)}
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>סגור</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
